import { Button } from '@1trading/react/components/ui/button';
import { Card, CardContent } from '@1trading/react/components/ui/card';
import { Checkbox } from '@1trading/react/components/ui/checkbox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { Textarea } from '@1trading/react/components/ui/textarea';
import { Plus, Trash2 } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { BINARY_ANSWERS } from '@/constants/answers';
import { NO_TIN_REASONS, NoTinReason } from '@/constants/institution';
import { InstitutionFieldCountry } from '@/features/institution/components/fields/country';
import {
  INSTITUTION_ENTITY_TAX_RESIDENCY_DEFAULTS,
  type InstitutionTaxSelfCertification,
  type InstitutionTaxSelfCertificationOrDefaults,
  TAX_RESIDENCIES_MAX,
} from '@/features/institution/tax-self-certification/schema';

const InstitutionTaxSelfCertificationFieldsetResidencyLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.taxSelfCertification.residency.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.taxSelfCertification.residency.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionTaxSelfCertificationFieldsetResidencyLegend.displayName = 'InstitutionTaxSelfCertificationFieldsetResidencyLegend';

const InstitutionTaxSelfCertificationFieldsetTaxResidency = ({ index, onRemove }: { index: number; onRemove?: () => void }) => {
  const form = useFormContext<InstitutionTaxSelfCertificationOrDefaults>();
  const { t } = useTranslation();
  const [hasNoTin, noTinReason] = form.watch([`taxResidencies.${index}.hasNoTin`, `taxResidencies.${index}.noTinReason`]);

  return (
    <Card className="shadow-none" bordered>
      <CardContent className="pt-6">
        <fieldset className="relative space-y-6">
          <legend className="font-medium">
            {t('institutionDetails.taxSelfCertification.residency.taxResidence')} {index + 1}
          </legend>
          {onRemove ? (
            <Button className="absolute top-[-0.66rem] right-[-0.66rem] !m-0 text-destructive hover:text-destructive" size="icon" type="button" variant="ghost" onClick={onRemove}>
              <Trash2 className="size-4" />
              <span className="sr-only">
                {t('institutionDetails.taxSelfCertification.residency.remove')} {index + 1}
              </span>
            </Button>
          ) : null}
          <InstitutionFieldCountry<InstitutionTaxSelfCertificationOrDefaults>
            label={t('institutionDetails.taxSelfCertification.residency.jurisdiction')}
            name={`taxResidencies.${index}.jurisdiction`}
            placeholder={t('institutionDetails.taxSelfCertification.placeholders.jurisdiction')}
          />
          {hasNoTin ? null : (
            <FormField
              control={form.control}
              name={`taxResidencies.${index}.tin`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('institutionDetails.taxSelfCertification.residency.tin')}*</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t('institutionDetails.taxSelfCertification.placeholders.tin')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormField
            control={form.control}
            name={`taxResidencies.${index}.hasNoTin`}
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-row items-center gap-4">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormLabel>{t('institutionDetails.taxSelfCertification.residency.hasNoTin')}</FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          {hasNoTin ? (
            <FormField
              control={form.control}
              name={`taxResidencies.${index}.noTinReason`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('institutionDetails.taxSelfCertification.residency.noTinReason')}*</FormLabel>
                  <FormControl>
                    <RadioGroup
                      defaultValue={field.value}
                      variant="cards"
                      aria-label={t('institutionDetails.taxSelfCertification.residency.noTinReason')}
                      onValueChange={field.onChange}
                    >
                      {NO_TIN_REASONS.map((reason) => (
                        <FormItem key={reason}>
                          <RadioGroupItemCard variant="outline">
                            <FormControl>
                              <RadioGroupItem value={reason} />
                            </FormControl>
                            <FormLabel contained>{t(`institutionDetails.taxSelfCertification.residency.noTinReasons.${reason}`)}</FormLabel>
                          </RadioGroupItemCard>
                        </FormItem>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}
          {hasNoTin && noTinReason === NoTinReason.UNABLE_TO_OBTAIN ? (
            <FormField
              control={form.control}
              name={`taxResidencies.${index}.noTinExplanation`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('institutionDetails.taxSelfCertification.residency.noTinExplanation')}*</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder={t('institutionDetails.taxSelfCertification.placeholders.noTinExplanation')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}
        </fieldset>
      </CardContent>
    </Card>
  );
};
InstitutionTaxSelfCertificationFieldsetTaxResidency.displayName = 'InstitutionTaxSelfCertificationFieldsetTaxResidency';

const InstitutionTaxSelfCertificationFieldsetResidency = () => {
  const form = useFormContext<InstitutionTaxSelfCertificationOrDefaults>();
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'taxResidencies' });
  const { t } = useTranslation();
  const noTaxResidence = form.watch('noTaxResidence');

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="noTaxResidence"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.taxSelfCertification.residency.noTaxResidence')}*</FormLabel>
            <FormControl>
              <RadioGroup
                defaultValue={field.value}
                variant="cards"
                aria-label={t('institutionDetails.taxSelfCertification.residency.noTaxResidence')}
                onValueChange={field.onChange}
              >
                {BINARY_ANSWERS.map((answer) => (
                  <FormItem key={answer}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={answer} />
                      </FormControl>
                      <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {noTaxResidence === 'NO' ? (
        <FormField
          control={form.control}
          name="taxResidencies"
          render={({ fieldState: { error } }) => (
            <FormItem>
              <div className="space-y-6">
                {fields.map((residency, index) => (
                  <InstitutionTaxSelfCertificationFieldsetTaxResidency key={residency.id} index={index} onRemove={fields.length > 1 ? () => remove(index) : undefined} />
                ))}
                {fields.length < TAX_RESIDENCIES_MAX ? (
                  <Button className="gap-2" size="sm" type="button" variant="outline" onClick={() => append({ ...INSTITUTION_ENTITY_TAX_RESIDENCY_DEFAULTS })}>
                    <Plus className="size-4" />
                    {t('institutionDetails.taxSelfCertification.residency.add')}
                  </Button>
                ) : null}
              </div>
              {error?.root?.message || error?.message ? <FormMessage>{error.root?.message ?? error.message}</FormMessage> : null}
            </FormItem>
          )}
        />
      ) : null}
      {noTaxResidence === 'YES' ? (
        <InstitutionFieldCountry<InstitutionTaxSelfCertification>
          label={t('institutionDetails.taxSelfCertification.residency.placeOfEffectiveManagement')}
          name="placeOfEffectiveManagement"
          placeholder={t('institutionDetails.taxSelfCertification.placeholders.placeOfEffectiveManagement')}
        />
      ) : null}
    </div>
  );
};
InstitutionTaxSelfCertificationFieldsetResidency.displayName = 'InstitutionTaxSelfCertificationFieldsetResidency';

export { InstitutionTaxSelfCertificationFieldsetResidency, InstitutionTaxSelfCertificationFieldsetResidencyLegend };
